import { Link } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Sparky } from "../components/Sparky";
import { Sparkles, Rocket, Bot, Eye, Workflow, GraduationCap, MessageSquare, Globe2, Check, X } from "lucide-react";

function Blob({ className, color }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute rounded-full blur-3xl opacity-60 animate-blob ${className}`}
      style={{ background: color }}
    />
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-3xl md:text-4xl font-black tracking-tight text-[#1A1A1A]">{value}</span>
      <span className="text-sm font-semibold text-black/60 mt-1">{label}</span>
    </div>
  );
}

function StepCard({ n, title, desc, color, icon, testid }) {
  return (
    <div
      className="relative rounded-3xl bg-white border-2 border-black/5 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
      data-testid={testid}
    >
      <div
        className="inline-grid place-items-center h-14 w-14 rounded-2xl mb-5 text-white shadow-[0_4px_0_rgba(0,0,0,0.15)]"
        style={{ background: color }}
      >
        {icon}
      </div>
      <div className="text-xs font-black text-black/40 mb-2">STEP {n}</div>
      <h3 className="text-2xl font-extrabold text-[#1A1A1A]">{title}</h3>
      <p className="mt-2 text-black/70 leading-relaxed">{desc}</p>
    </div>
  );
}

function IdeaCard({ title, desc, className, color, icon, testid }) {
  return (
    <div
      className={`relative rounded-3xl p-8 border-2 border-black/5 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] ${className}`}
      style={{ background: color }}
      data-testid={testid}
    >
      <div className="inline-grid place-items-center h-12 w-12 rounded-2xl mb-4 bg-black/85 text-white">
        {icon}
      </div>
      <h3 className="text-2xl font-extrabold text-[#1A1A1A]">{title}</h3>
      <p className="mt-2 text-black/75 leading-relaxed">{desc}</p>
    </div>
  );
}

function AgeCard({ title, ages, desc, color, testid }) {
  return (
    <div
      className="rounded-3xl p-8 border-2 border-black/5 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
      style={{ background: color }}
      data-testid={testid}
    >
      <div className="text-xs font-black text-black/50">{ages}</div>
      <h3 className="mt-1 text-3xl font-black text-[#1A1A1A]">{title}</h3>
      <p className="mt-3 text-black/80 leading-relaxed">{desc}</p>
    </div>
  );
}

export default function Home() {
  const { t, lang } = useI18n();

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]" data-testid="home-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <Blob className="w-[420px] h-[420px] -top-24 -left-24" color="#4CC9F0" />
        <Blob className="w-[380px] h-[380px] top-24 -right-24" color="#F72585" />
        <Blob className="w-[280px] h-[280px] bottom-0 left-1/3" color="#FFB703" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-16 md:pt-24 pb-24 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-start">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/5 px-4 py-2 text-sm font-bold text-black/70 shadow-[0_2px_0_rgba(0,0,0,0.05)]">
              <Sparkles className="h-4 w-4 text-[#F72585]" />
              {t("hero.badge")}
            </div>

            <h1
              className="mt-6 text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02]"
              data-testid="hero-title"
            >
              {t("hero.title1")}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{t("hero.title2")}</span>
                <span
                  aria-hidden
                  className="absolute inset-x-0 -bottom-2 h-4 rounded-full"
                  style={{ background: "#FFB703" }}
                />
              </span>{" "}
              <span className="inline-block">🤖</span>
              <span className="inline-block">✨</span>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-black/70 font-medium max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-full bg-[#4361EE] text-white font-bold px-7 py-4 shadow-[0_4px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_3px_0_#2A3EB1] transition"
                data-testid="hero-cta-demo"
              >
                <Rocket className="h-5 w-5" />
                {t("hero.ctaDemo")}
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-7 py-4 shadow-[0_4px_0_#000] hover:translate-y-[1px] hover:shadow-[0_3px_0_#000] transition"
                data-testid="hero-cta-start"
              >
                {t("hero.ctaStart")}
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat label={t("hero.statAges")} value="10–16" testid="stat-ages" />
              <Stat label={t("hero.statIdeas")} value="5" testid="stat-ideas" />
              <Stat label={t("hero.statSafety")} value="🛡️" testid="stat-safety" />
            </div>
          </div>

          <div className="relative flex items-center justify-center">
            <div className="relative">
              <div
                aria-hidden
                className="absolute inset-0 rounded-[3rem] rotate-6"
                style={{ background: "linear-gradient(135deg,#F72585,#4CC9F0)" }}
              />
              <div className="relative rounded-[3rem] bg-white p-8 border-2 border-black/5 shadow-[0_30px_60px_rgba(0,0,0,0.10)]">
                <div className="animate-float">
                  <Sparky size={280} />
                </div>
                <div className="absolute -bottom-4 start-6 rounded-full bg-[#FFB703] px-4 py-2 text-sm font-black shadow-[0_3px_0_rgba(0,0,0,0.15)]">
                  {lang === "ar" ? "مرحباً! أنا سباركي" : "Hi! I'm Sparky"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight">{t("how.title")}</h2>
          <p className="mt-4 text-lg text-black/70 font-medium">{t("how.subtitle")}</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <StepCard n="01" title={t("how.s1t")} desc={t("how.s1d")} color="#4361EE" icon={<Eye className="h-6 w-6" />} testid="step-discover" />
          <StepCard n="02" title={t("how.s2t")} desc={t("how.s2d")} color="#F72585" icon={<Workflow className="h-6 w-6" />} testid="step-play" />
          <StepCard n="03" title={t("how.s3t")} desc={t("how.s3d")} color="#4CC9F0" icon={<Bot className="h-6 w-6" />} testid="step-ask" />
        </div>
      </section>

      {/* FIVE BIG IDEAS */}
      <section id="ideas" className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight">{t("ideas.title")}</h2>
          <p className="mt-4 text-lg text-black/70 font-medium">{t("ideas.subtitle")}</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          <IdeaCard
            className="md:col-span-7"
            color="#FFF3C4"
            icon={<Eye className="h-5 w-5" />}
            title={t("ideas.i1t")}
            desc={t("ideas.i1d")}
            testid="idea-senses"
          />
          <IdeaCard
            className="md:col-span-5"
            color="#E7EEFE"
            icon={<Workflow className="h-5 w-5" />}
            title={t("ideas.i2t")}
            desc={t("ideas.i2d")}
            testid="idea-steps"
          />
          <IdeaCard
            className="md:col-span-5"
            color="#FBE1EE"
            icon={<GraduationCap className="h-5 w-5" />}
            title={t("ideas.i3t")}
            desc={t("ideas.i3d")}
            testid="idea-learn"
          />
          <IdeaCard
            className="md:col-span-4"
            color="#DFF6FD"
            icon={<MessageSquare className="h-5 w-5" />}
            title={t("ideas.i4t")}
            desc={t("ideas.i4d")}
            testid="idea-talks"
          />
          <IdeaCard
            className="md:col-span-3"
            color="#FCE3D3"
            icon={<Globe2 className="h-5 w-5" />}
            title={t("ideas.i5t")}
            desc={t("ideas.i5d")}
            testid="idea-world"
          />
        </div>
      </section>

      {/* GCC BAND */}
      <section className="my-16 md:my-24">
        <div
          className="mx-auto max-w-7xl rounded-[2.5rem] px-8 md:px-16 py-14 md:py-20 text-white relative overflow-hidden"
          style={{ background: "#E07A5F" }}
          data-testid="gcc-band"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-sm font-black tracking-widest uppercase opacity-80">{t("gcc.tagline")}</div>
              <p className="mt-4 text-2xl md:text-3xl font-extrabold leading-snug">{t("gcc.body")}</p>
            </div>
            <div className="text-6xl md:text-8xl font-black tracking-tighter text-end opacity-90" dir="rtl">
              علم
            </div>
          </div>
        </div>
      </section>

      {/* AGE BANDS */}
      <section id="ages" className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight">{t("ages.title")}</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <AgeCard title={t("ages.f1t")} ages={t("ages.f1a")} desc={t("ages.f1d")} color="#DFF6FD" testid="age-foundation" />
          <AgeCard title={t("ages.f2t")} ages={t("ages.f2a")} desc={t("ages.f2d")} color="#FFF3C4" testid="age-builder" />
          <AgeCard title={t("ages.f3t")} ages={t("ages.f3a")} desc={t("ages.f3d")} color="#E7EEFE" testid="age-innovator" />
        </div>
      </section>

      {/* SAFETY */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight">{t("safety.title")}</h2>
          <p className="mt-4 text-lg text-black/70 font-medium">{t("safety.subtitle")}</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-6" data-testid="safety-rulebook">
          <div className="rounded-3xl bg-white border-2 border-black/5 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#DCFCE7] text-[#166534] font-black px-4 py-2 text-sm">
              <Check className="h-4 w-4" /> {t("safety.doTitle")}
            </div>
            <ul className="mt-5 space-y-3">
              {t("safety.do").map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-black/80" data-testid={`safety-do-${i}`}>
                  <span className="mt-1 inline-grid place-items-center h-6 w-6 rounded-full bg-[#166534] text-white flex-shrink-0">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-white border-2 border-black/5 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FEE2E2] text-[#991B1B] font-black px-4 py-2 text-sm">
              <X className="h-4 w-4" /> {t("safety.dontTitle")}
            </div>
            <ul className="mt-5 space-y-3">
              {t("safety.dont").map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-black/80" data-testid={`safety-dont-${i}`}>
                  <span className="mt-1 inline-grid place-items-center h-6 w-6 rounded-full bg-[#991B1B] text-white flex-shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
